<template>
  <div class="editor-main h-full flex flex-col" @contextmenu.prevent="unSelectElement()">
    <div class="preview-frame flex-1 flex items-center justify-center p-4 relative" ref="previewFrame">

      <div 
        class="preview-container absolute bg-white rounded-lg shadow-xl" 
        :style="{...previewContainerStyle, transform: 'scale(' + previewScale + ')'}"
        ref="previewArea"
      >
        <div class="size-indicator-width">
          <div class="size-indicator-width-line"></div>
          <div class="size-indicator-width-text">{{ screenSize.width }} px</div>
          <div class="size-indicator-width-line"></div>
        </div>
        <div class="size-indicator-height">
          <div class="size-indicator-height-line"></div>
          <div class="size-indicator-height-text">{{ screenSize.height }} px</div>
          <div class="size-indicator-height-line"></div>
        </div>
        <!-- Message si pas de thème -->
        <div v-show="!currentPageIndex" class="absolute inset-0 flex items-center justify-center">
          <div class="text-gray-400 text-5xl flex flex-col items-center">
            <span class="text-6xl mb-4">📁</span>
            Aucun thème chargé
          </div>
        </div>

        <!-- Guidelines pour le snap -->
        <div v-if="snapData.enable" class="snap-guidelines">
          <div v-for="(pos, index) in snapData.guide.horizontal" :key="`h-${index}`" 
               class="snap-guideline horizontal"
               :class="{ 'active': snapData.snap.horizontal === pos }"
               :style="{ top: `${pos}px` }">
          </div>
          
          <div v-for="(pos, index) in snapData.guide.vertical" :key="`v-${index}`" 
               class="snap-guideline vertical"
               :class="{ 'active': snapData.snap.vertical === pos }"
               :style="{ left: `${pos}px` }">
          </div>
        </div>
        


        <!-- Éléments du thème -->
        <component class="media-element" v-if="currentPage" v-for="(element, index) in currentPage.toIterable()"
          :is="element.objectType"
          :key="index"
          :el="element"
          :style="{ zIndex: 100 + index }"
        ></component>
      </div>

            <!-- Bouton de désélection -->
      <button 
        v-if="currentElementIndex !== null"
        @click="unSelectElement"
        class="deselect-button"
      >
        Désélectionner
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import * as namespaceStore from "../../vuexStore/namespaceStore.js"
import * as mutates from "../../vuexStore/mutations.js"
import * as actions from "../../vuexStore/actions.js"
import ContainerItem from "./PreviewComponents/container.vue"
import PrintContainerItem from "./PreviewComponents/container.vue"
import DiaporamaItem from "./PreviewComponents/diaporama.vue"
import ImageItem from "./PreviewComponents/image.vue"
import ImageMedia from "./PreviewComponents/image.vue"
import TextItem from "./PreviewComponents/text.vue"
import ColorItem from "./PreviewComponents/color.vue"
import VideoItem from "./PreviewComponents/video.vue"
import screenSize from "../../vuexStore/type/definition/screenSize.js"

export default {
  name: 'PreviewArea',
  props: {
  },
  emits: [
  ],
  components: {
    ContainerItem,
    PrintContainerItem,
    DiaporamaItem,
    ImageItem,
    ImageMedia,
    TextItem,
    ColorItem,
    VideoItem
  },
  mounted() {
    window.addEventListener('mousemove', this.handleDrag);
    window.addEventListener('mouseup', this.stopDrag);
    this.resizeObserver = new ResizeObserver(this.updatePreviewScale);
    this.resizeObserver.observe(this.$refs.previewFrame);
  },
  beforeUnmount() {
    window.removeEventListener('mousemove', this.handleDrag);
    window.removeEventListener('mouseup', this.stopDrag);
    this.resizeObserver.disconnect();
  },
  data() {
    return {
      resizeObserver: null
    }
  },
  watch: {
    currentPage: {
      handler(newVal) {
        if (newVal) {
          newVal.playAnimationDeep = true;
        }
      }
    }
  },
  computed: {
    ...mapGetters(namespaceStore.THEME, [
      'snapData',
      'current',
      'currentElementIndex',
      'currentPage',
      'currentPageIndex',
      'pages',
      'previewScale'
    ]),
    previewContainerStyle: function() {
      return {
        width: screenSize.width + "px",
        height: screenSize.height + "px",
        maxWidth: screenSize.width + "px",
        maxHeight: screenSize.height + "px",
      }
    },
    screenSize: function() {
      return screenSize;
    }
  },
  methods: {
    ...mapMutations(namespaceStore.THEME, {
      setCurrentElement: mutates.SET_CURRENT_ELEMENT,
      handleDrag: mutates.HANDLE_DRAG,
      setPreviewScale: mutates.SET_PREVIEW_SCALE,
      unSelectElement: mutates.UNSELECT_ELEMENT,
    }),
    ...mapActions(namespaceStore.THEME, {
      stopDrag: actions.STOP_DRAG
    }),
    updatePreviewScale() {
      const widthRatio = this.$refs.previewFrame.clientWidth / (screenSize.width + 400);
      const heightRatio = this.$refs.previewFrame.clientHeight / (screenSize.height + 400);  

      const ratio = Math.min(widthRatio, heightRatio);
      this.setPreviewScale(ratio);
    },
    handleElementClick(elementIndex) {
      if (this.currentElementIndex && this.currentElementIndex === elementIndex) {
        return;
      }
      this.setCurrentElement(elementIndex);
    },
  }
}
</script>

<style scoped>
.size-indicator-width {
  height: 75px;
  position: absolute;
  top: -175px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 50px;
}

.size-indicator-height {
  width: 75px;
  position: absolute;
  top: 0;
  left: -175px;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 50px;
  flex-direction: column;
}

.size-indicator-width-line, .size-indicator-height-line {
  flex: 1;
  border: 2px solid white;
}

.size-indicator-width-text {
  font-size: 50px;
  color: white;
}

.size-indicator-height-text {
  font-size: 50px;
  writing-mode: sideways-lr;
  color: white;
}

.editor-main {
  @apply relative h-full w-full;
}

.preview-frame {
  scrollbar-width: thin;
  min-height: 500px;
  min-width: 100%;
}

.media-element {
  @apply absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-container {
  background-color: white;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  transform-origin: center;
}

.preview-container img {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.snap-guidelines {
  @apply absolute inset-0 pointer-events-none;
}

.snap-guideline {
  @apply absolute bg-blue-500 opacity-30;
  transition: opacity 0.2s ease;
}

.snap-guideline.horizontal {
  @apply w-full h-px;
}

.snap-guideline.vertical {
  @apply h-full w-px;
}

.snap-guideline.active {
  @apply opacity-100;
}

.element-wrapper {
  @apply border-2 border-transparent;
  transition: border-color 0.2s ease;
}

.element-wrapper.selected {
  @apply border-blue-500;
}

.resize-handles {
  @apply absolute inset-0 pointer-events-none;
}

.resize-handle {
  @apply absolute w-3 h-3 bg-white border-2 border-blue-500 rounded-full pointer-events-auto cursor-nw-resize;
  margin: -6px;
}

.resize-handle.top-left {
  @apply top-0 left-0;
}

.resize-handle.top-right {
  @apply top-0 right-0;
  cursor: ne-resize;
}

.resize-handle.bottom-left {
  @apply bottom-0 left-0;
  cursor: sw-resize;
}

.resize-handle.bottom-right {
  @apply bottom-0 right-0;
  cursor: se-resize;
}

.text-element, .image-element, .button-element, .special-button-element {
  @apply overflow-hidden;
  pointer-events: none;
}

.image-element img {
  @apply pointer-events-none;
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.deselect-button {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.5rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  color: #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.deselect-button:hover {
  background-color: rgba(255, 255, 255, 0.15);
  border-color: #3b82f6;
  color: #3b82f6;
}

.deselect-button:focus {
  outline: none;
  border-color: #3b82f6;
  background-color: rgba(255, 255, 255, 0.15);
}
</style> 